import React, { useState, useRef, useEffect } from 'react';
import { useUser } from '../userContext';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

export default function Profile() {
  const { user } = useUser();
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (user) {
      async function fetchUserProfile() {
        const userDocRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setFirstName(data.firstName);
          setLastName(data.lastName);
        }
      }
      fetchUserProfile();
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (user) {
      const userDocRef = doc(db, 'users', user.uid);
      try {
        await updateDoc(userDocRef, { firstName, lastName });
        setUnsavedChanges(false);
        setSaveSuccess(true);
      } catch (error) {
        console.error("Error updating profile: ", error);
      } finally {
        setTimeout(() => setSaveSuccess(false), 3000); // Clear success badge after 3 seconds
      }
    }
  };

  return (
    <div className="w-full px-8 py-6" style={{fontFamily: '"Share Tech Mono", monospace'}}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-12 sm:space-y-16">
          <div>
            <div className="flex items-center">
              <h2 className="text-base font-black leading-7 tracking-wide" style={{
                color: '#F1C40F',
                textShadow: '0 0 4px rgba(241, 196, 15, 0.3)'
              }}>
                ACCOUNT
              </h2>
              {unsavedChanges && (
                <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold" style={{
                  background: 'rgba(231, 76, 60, 0.2)',
                  border: '1px solid rgba(231, 76, 60, 0.4)',
                  color: '#E74C3C'
                }}>
                  UNSAVED CHANGES
                </span>
              )}
              {saveSuccess && (
                <span className="ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold" style={{
                  background: 'rgba(46, 204, 113, 0.2)',
                  border: '1px solid rgba(46, 204, 113, 0.4)',
                  color: '#2ECC71'
                }}>
                  CHANGES SAVED
                </span>
              )}
            </div>
            <p className="mt-1 max-w-2xl text-sm leading-6 font-bold" style={{color: '#BDC3C7'}}>
              Change your personal information.
            </p>
            <div className="mt-5 space-y-4 mb-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="first-name" className="block text-sm font-bold leading-6" style={{color: '#ECF0F1'}}>
                    FIRST NAME
                  </label>
                  <div className="mt-2">
                    <div className="flex rounded-lg shadow-sm ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset w-full" style={{
                      background: 'rgba(52, 73, 94, 0.6)',
                      border: '1px solid rgba(241, 196, 15, 0.4)',
                      focusWithin: {
                        borderColor: '#F1C40F'
                      }
                    }}>
                      <input
                        type="text"
                        name="first-name"
                        id="first-name"
                        maxLength="20"
                        autoComplete="given-name"
                        className="block flex-1 border-0 bg-transparent ml-2 py-1.5 pl-1 placeholder:text-gray-400 focus:ring-0 text-md sm:text-sm sm:leading-6 font-bold"
                        style={{
                          color: '#ECF0F1',
                          fontFamily: '"Share Tech Mono", monospace'
                        }}
                        value={firstName}
                        required
                        onChange={(e) => {
                          setFirstName(e.target.value);
                          setUnsavedChanges(true);
                          setSaveSuccess(false);
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="last-name" className="block text-sm font-bold leading-6" style={{color: '#ECF0F1'}}>
                    LAST NAME
                  </label>
                  <div className="mt-2">
                    <div className="flex rounded-lg shadow-sm ring-1 ring-inset focus-within:ring-2 focus-within:ring-inset w-full" style={{
                      background: 'rgba(52, 73, 94, 0.6)',
                      border: '1px solid rgba(241, 196, 15, 0.4)'
                    }}>
                      <input
                        type="text"
                        name="last-name"
                        id="last-name"
                        maxLength="20"
                        autoComplete="family-name"
                        className="block flex-1 border-0 bg-transparent ml-2 py-1.5 pl-1 placeholder:text-gray-400 focus:ring-0 text-md sm:text-sm sm:leading-6 font-bold"
                        style={{
                          color: '#ECF0F1',
                          fontFamily: '"Share Tech Mono", monospace'
                        }}
                        value={lastName}
                        required
                        onChange={(e) => {
                          setLastName(e.target.value);
                          setUnsavedChanges(true);
                          setSaveSuccess(false);
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="sm:col-span-4">
                <label htmlFor="email" className="block text-sm font-bold leading-6" style={{color: '#ECF0F1'}}>
                  EMAIL
                </label>
                <div className="mt-2" style={{
                  background: 'rgba(52, 73, 94, 0.3)'
                }}>
                  <div className="flex rounded-lg shadow-sm ring-1 ring-inset w-full" style={{
                    background: 'rgba(52, 73, 94, 0.3)',
                    border: '1px solid rgba(149, 165, 166, 0.3)'
                  }}>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      className="block flex-1 border-0 bg-transparent ml-2 py-1.5 pl-1 focus:ring-0 text-md sm:text-sm sm:leading-6 cursor-default font-bold"
                      style={{
                        color: '#95A5A6',
                        fontFamily: '"Share Tech Mono", monospace'
                      }}
                      value={user.email}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-start">
              <button
                type="submit"
                disabled={!unsavedChanges}
                className={`inline-flex justify-center rounded-lg px-6 py-3 text-sm font-bold shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 w-full sm:w-auto transition-all duration-100 ${
                  unsavedChanges ? '' : 'cursor-not-allowed'
                }`}
                style={unsavedChanges ? {
                  background: 'linear-gradient(135deg, #E74C3C, #C0392B)',
                  border: '2px solid rgba(241, 196, 15, 0.6)',
                  color: '#FFFFFF',
                  textShadow: '1px 1px 0px #A93226'
                } : {
                  background: 'rgba(52, 73, 94, 0.6)',
                  border: '1px solid rgba(149, 165, 166, 0.3)',
                  color: '#95A5A6'
                }}
                onMouseEnter={(e) => {
                  if (unsavedChanges) {
                    e.target.style.background = 'linear-gradient(135deg, #C0392B, #A93226)';
                    e.target.style.transform = 'translateY(-1px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (unsavedChanges) {
                    e.target.style.background = 'linear-gradient(135deg, #E74C3C, #C0392B)';
                    e.target.style.transform = 'translateY(0)';
                  }
                }}
              >
                SAVE
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
